import { useState, useEffect, useCallback } from 'react'
import { getEmailSignatures, createEmailSignature, updateEmailSignature, deleteEmailSignature } from 'team/apis/teamEmailSignatureApi'

/**
 * Custom hook to manage email signature templates
 * @param {string} teamId - Team ID
 * @returns {Object} { emailSignatureTemplates, templatesCount, loading, error, refreshTemplates, createTemplate, updateTemplate, deleteTemplate }
 */
export const useEmailSignatures = (teamId) => {
    const [emailSignatureTemplates, setEmailSignatureTemplates] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const templatesCount = emailSignatureTemplates.length

    // Fetch templates from API
    const fetchTemplates = useCallback(async () => {
        if (!teamId) {
            setEmailSignatureTemplates([])
            return
        }

        try {
            setLoading(true)
            setError(null)
            const response = await getEmailSignatures(teamId)
            if (response.success) {
                setEmailSignatureTemplates(response.emailSignatures || [])
            } else {
                throw new Error(response.message || 'Failed to fetch templates')
            }
        } catch (err) {
            console.error('Error fetching email signature templates:', err)
            setError(err.message)
            setEmailSignatureTemplates([])
        } finally {
            setLoading(false)
        }
    }, [teamId])

    // Fetch templates on mount and when teamId changes
    useEffect(() => {
        fetchTemplates()
    }, [fetchTemplates])

    // Create template
    const createTemplate = useCallback(async (templateData) => {
        if (!teamId) {
            throw new Error('Team ID is required')
        }
        try {
            setLoading(true)
            setError(null)
            const response = await createEmailSignature(teamId, templateData)
            if (response.success) {
                await fetchTemplates() // Refresh list
                return response
            } else {
                throw new Error(response.message || 'Failed to create template')
            }
        } catch (err) {
            console.error('Error creating email signature template:', err)
            setError(err.message)
            throw err
        } finally {
            setLoading(false)
        }
    }, [teamId, fetchTemplates])

    // Update template
    const updateTemplate = useCallback(async (templateId, updateData) => {
        if (!teamId) {
            throw new Error('Team ID is required')
        }
        try {
            setLoading(true)
            setError(null)
            const response = await updateEmailSignature(teamId, templateId, updateData)
            if (response.success) {
                await fetchTemplates() // Refresh list
                return response
            } else {
                throw new Error(response.message || 'Failed to update template')
            }
        } catch (err) {
            console.error('Error updating email signature template:', err)
            setError(err.message)
            throw err
        } finally {
            setLoading(false)
        }
    }, [teamId, fetchTemplates])

    // Delete template
    const deleteTemplate = useCallback(async (templateId) => {
        if (!teamId) {
            throw new Error('Team ID is required')
        }
        try {
            setLoading(true)
            setError(null)
            const response = await deleteEmailSignature(teamId, templateId)
            if (response.success) {
                await fetchTemplates() // Refresh list
                return response
            } else {
                throw new Error(response.message || 'Failed to delete template')
            }
        } catch (err) {
            console.error('Error deleting email signature template:', err)
            setError(err.message)
            throw err
        } finally {
            setLoading(false)
        }
    }, [teamId, fetchTemplates])

    return {
        emailSignatureTemplates,
        templatesCount,
        loading,
        error,
        refreshTemplates: fetchTemplates,
        createTemplate,
        updateTemplate,
        deleteTemplate
    }
}

